import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import _ from 'lodash';
import qs from 'query-string';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ReactResizeDetector from 'react-resize-detector';

import { COURSE_ENTITY_NAME, DOCUMENT_ENTITY_NAME } from './constants';
import BackButton from '../../components/buttons/BackButton';
import LazyText from '../../components/LazyText';
import Link from '../../components/Link';
import RecordRevision from '../../components/RecordRevision';
import UserDataRecord from '../../components/UserDataRecord';
import { ENTITY_DISPLAY } from '../../constants/Urls';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { selectDocumentData } from '../../selectors/DocumentViewer';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 15,
    paddingLeft: 30,
    paddingBottom: 20,
    justifyContent: 'space-between',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '48px',
    fontFamily: 'Proxima Nova',
    fontSize: '32px',
    fontWeight: 600,
    lineHeight: 1,
    marginTop: '10px',
    marginRight: '15px',
  },
  link: {
    color: '#297dfd',
  },
  buttonsContainer: {
    display: 'flex',
    marginRight: 14,
    marginTop: 10,
  },
  headerMobile: {
    display: 'flex',
    paddingTop: 15,
    paddingLeft: 30,
    paddingBottom: 20,
    justifyContent: 'start',
    flexDirection: 'column',
  },
  titleMobile: {
    display: 'flex',
    minHeight: '48px',
    fontFamily: 'Proxima Nova',
    fontSize: '32px',
    fontWeight: 600,
    lineHeight: 1,
    marginTop: '10px',
    marginRight: '15px',
    justifyContent: 'start',
    flexDirection: 'column',
  },
});

const Header = ({ reqId, isDocument, setHeaderHeight }) => {
  const classes = useStyles();
  const documentData = useSelector(selectDocumentData);

  const { isMobileDevice } = useWindowDimensions();

  const documentTitle = documentData.DOC_TITLE;
  const documentNumber = documentData.DOC_DOCUMENT_NO;
  const documentRevision = documentData.DOC_DOCUMENT_REV;

  const titleText = `${documentNumber ? `${documentNumber}` : ''}${
    documentRevision ? ` [${documentRevision}]` : ''
  }`;
  const entityDisplayParams = {
    entityType: isDocument ? DOCUMENT_ENTITY_NAME : COURSE_ENTITY_NAME,
    formKey: isDocument ? `${DOCUMENT_ENTITY_NAME}_DETAIL` : `${COURSE_ENTITY_NAME}_DETAIL`,
    itmId: reqId,
  };

  useEffect(() => {
    document.title = titleText;
  }, [titleText]);

  const onResizeHeader = useCallback(
    (w, h) => {
      setHeaderHeight(h);
    },
    [setHeaderHeight]
  );

  return (
    <div className={isMobileDevice ? classes.headerMobile : classes.header}>
      <Grid item>
        <div className={isMobileDevice ? classes.titleMobile : classes.title}>
          {_.isNil(reqId) ? (
            <LazyText text={titleText}>
              <span>{titleText}</span>
            </LazyText>
          ) : (
            <>
              <UserDataRecord
                handleFavorite={false}
                handleRecentEvent={false}
                queryStringParams={entityDisplayParams}
                recordNumber={documentNumber}
                revisionNumber={documentRevision}
              />
              <Link
                className={classes.link}
                to={{ pathname: ENTITY_DISPLAY, search: qs.stringify(entityDisplayParams) }}
              >
                <span ui-ta="RecordNumber">{documentNumber ?? ''}</span>
                {documentRevision && <RecordRevision revision={documentRevision} />}
              </Link>
              {documentTitle && <span>{`: ${documentTitle}`}</span>}
            </>
          )}
        </div>
      </Grid>
      <Grid item>
        {!isMobileDevice && <BackButton isCertificationPage />}
        <div id="file-viewer-buttons-container" className={classes.buttonsContainer} />
      </Grid>
      {isMobileDevice && <ReactResizeDetector handleHeight onResize={onResizeHeader} />}
    </div>
  );
};

export default Header;
